<template>
  <div
    @contextmenu="selectComponentDEV($event, schema.model)"
    :class="['d-flex', getSelectedDEV && 'dev-selected-component']"
    :key="generateId(schema.model)"
  >
    <i4pro-icon-popover
      v-if="schema.attributesInfo"
      :class="[schema.attributesInfo.right ? 'ml-1 order-2' : 'mr-1']"
      :schema="schema"
      :model="model"
    ></i4pro-icon-popover>

    <v-menu :close-on-content-click="false" v-model="menu" v-bind="schema.attributesMenu" ref="menu">
      <template v-slot:activator="{ on }">
        <v-text-field
          v-bind="schema.attributesTextField"
          :disabled="schema.readonly"
          :value="inputValue"
          :rules="rulesWithDate"
          :name="schema.model"
          :id="generateId(schema.model)"
          @click:clear="clear"
          @change="onChangeInput"
          v-on="!schema.pickerAsButton && on"
          v-mask="localizedMask()"
        >
          <template v-slot:label>
            {{ schema.attributesTextField.label }}
            <span v-if="schema.rules.includes('required')" class="red--text">*</span>
          </template>
          <template v-if="schema.pickerAsButton" v-slot:append-outer>
            <v-icon v-on="on" :color="schema?.pickerColor ?? 'secondary'">{{schema.pickerAsButtonIcon || 'mdi-calendar'}}</v-icon>
          </template>
        </v-text-field>
      </template>
      <v-date-picker
        @focus="onFocus"
        @change="onChange"
        @input="menu = false"
        v-model="datePickerValue"
        v-bind="schema.attributesDatePicker"
        :disabled="schema.readonly"
        :min="valueDateMin"
        :max="valueDateMax"
        v-if="!schema.attributesTextField.readonly"
      >
      <v-spacer></v-spacer>
       <v-btn v-if="schema.btnShow && schema.btnTodayLabel" :disabled="schema.readonly" :text="schema.btnFlat" :color="schema.btnTodayColor" @click="setToday">{{
          schema.btnTodayLabel
        }}</v-btn>

        <v-btn v-if="schema.btnShow" :text="schema.btnFlat" :color="schema.btnCancelColor" @click="menu = false">{{
          schema.btnCancelLabel
        }}</v-btn>

        <v-btn
          v-if="schema.btnShow && schema.btnSaveLabel"
          :disabled="schema.readonly"
          :text="schema.btnFlat"
          :color="schema.btnSaveColor"
          @click="$refs.menu.save(value)"
          >{{ schema.btnSaveLabel }}</v-btn>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import { abstractField } from 'vue-form-generator';
import devComponentMixin from '@/mixins/dev-component-mixin';
import rulesMixin from '@/mixins/rules-mixin';
import moment from 'moment';
import inputEventsMixin from '@/mixins/input-events-mixin';
import componentMixin from '@/mixins/component-mixin';
import { compilerOperationDate } from '@/helpers/utils';

export default {
  mixins: [abstractField, devComponentMixin, rulesMixin, inputEventsMixin, componentMixin],

  components: {
    i4proIconPopover: () => import('@/components/icons/i4pro-icon-popover')
  },

  data: () => ({
    menu: false,
    datePickerValue: null,
    options: {
      locale: ''
    },
    defaultOptions: {
      locale: 'pt-br'
    }
  }),

  created() {
    Object.assign(this.options, this.defaultOptions, this.schema.attributesDatePicker);
    this.setupPicker(this.value);
  },
  computed: {
    inputValue() {
      if (this.value !== null && this.value !== '' && this.value !== undefined) {
        moment.locale(this.options.locale);
        let m = moment(this.value);
        return m.isValid() ? m.format('L') : null;
      }
      return null;
    },
    rulesWithDate() {
      let r = this.rules;
      r.push(this.validateDate);
      return r;
    },
    valueDateMin() {
      if (('rangeDate' in this.schema) && ('min' in this.schema.rangeDate)) {
        let minValue = this.schema.rangeDate.min

        if (this.isDynamicRangeValue(minValue)) {
          const modelValue = this.getDynamicRangeValue('_rangemin')
          if (modelValue) minValue = modelValue
        }
        
        const filledAllDaysOnMonth = ('filledAllDaysOnMonth' in this.schema.rangeDate && this.schema.rangeDate.filledAllDaysOnMonth);
        return compilerOperationDate(minValue, filledAllDaysOnMonth);
      }

      return undefined;
    },
    valueDateMax() {
      if (('rangeDate' in this.schema) && ('max' in this.schema.rangeDate)) {
        let maxValue = this.schema.rangeDate.max

        if (this.isDynamicRangeValue(maxValue)) {
          const modelValue = this.getDynamicRangeValue('_rangemax')
          if (modelValue) maxValue = modelValue
        }

        const filledAllDaysOnMonth = ('filledAllDaysOnMonth' in this.schema.rangeDate && this.schema.rangeDate.filledAllDaysOnMonth);
        return compilerOperationDate(maxValue, filledAllDaysOnMonth);
      }

      return undefined;
    },
  },

  watch: {
    value(newValue, oldValue) {
      if (this.checkDateValidInRange(newValue)) {
        this.clear();
        return;
      }
      if (newValue !== oldValue) {
        this.setupPicker(newValue);
        this.changeEvents();
      }
    }
  },

  methods: {
    validateDate: function (v) {
      if (v === null || v === undefined || v === '' || v.length === 0) return true;

      return moment(v, this.localizedFormat()).isValid() ? true : 'Data inválida';
    },

    setupPicker: function (newValue) {
      if (!newValue) {
        this.datePickerValue = null;
        return;
      }

      if (this.value === this.datePickerValue) return;

      let m = moment(newValue);
      let v = m.isValid() ? m.toISOString().substring(0, 10) : null;
      this.datePickerValue = v;
    },

    onChange: function () {
      this.value = this.datePickerValue;
    },

    localizedMask: function () {
      const formatParts = /d|D|m|M|y|Y/g;
      return this.localizedFormat().replace(formatParts, '#');
    },

    localizedFormat: function () {
      moment.locale(this.options.locale);
      return moment().creationData().locale.longDateFormat('L');
    },

    checkDateValidInRange: function (currentDateToCheck) {
      return ( (this.valueDateMin !== undefined && !moment(this.valueDateMin).isSameOrBefore(currentDateToCheck)) ||
        (this.valueDateMax !== undefined && !moment(this.valueDateMax).isSameOrAfter(currentDateToCheck)) )
    },

    onChangeInput: function (newValue) {
      if (!newValue) {
        this.value = null;
        return;
      }

      let m = moment(newValue, this.localizedFormat());
      this.value = m.isValid() ? m.toISOString().substring(0, 10) : null;
    },

    setToday: function(){
      const today = moment().toISOString().substring(0,10);
      
      if (this.checkDateValidInRange(today)) {
        return;
      }

      this.value = today;
      this.menu = false;
    },

    clear: function () {
      this.$nextTick(() => (this.value = null));
    },

    isDynamicRangeValue(value) {
      // verifica se valor está entre chaves {...}
      const regex = /^\{(.+)\}$/;
      return !!value.match(regex);
    },

    getDynamicRangeValue(suffix) {
      const field = `${this.schema.model}${suffix}`
      const { page, mode } = this.formOptions
      const params = { page, mode, field }
      return this.$store.getters['page/modelValue'](params)
    },
  }
};
</script>
